import { GameState, DailyStats, Neighborhood, WeatherState } from '../domain/types.ts';
import { CryptoStore } from './cryptoStore.ts';

const SAVE_KEY = 'bachuhe_save_v3';
const LEGACY_SAVE_KEY = 'bachuhe_save_v2';
let localSaveSeq = 0;

export interface PersistentSave {
  version: number;
  savedAt: number;
  phase?: GameState['phase'] | undefined;
  player: GameState['player'];
  stalls: GameState['stalls'];
  neighborhood: Neighborhood | null;
  seed: number;
  dayNumber: number;
  dayStats?: DailyStats | undefined;
  dayHistory: DailyStats[];
  totalRevenue: number;
  totalProfit: number;
  totalSales: number;
  dummyRivals?: Record<string, import('../domain/types.ts').DummyRival> | undefined;
  weather?: WeatherState | undefined;
  currentShift?: import('../domain/types.ts').DayShift | undefined;
  isWeekend?: boolean | undefined;
  isOvertimeActive?: boolean | undefined;
  dayOfWeek?: string | undefined;
  narrativeQuote?: string | undefined;
  currentTrendingTopic?: string | undefined;
  trendingItems?: import('../domain/types.ts').TrendItem[] | undefined;
  saveSeq?: number | undefined;
  secretKey?: string | undefined;
}

export class SaveStore {
  public static save(state: GameState): boolean {
    try {
      localSaveSeq++;

      // Tự động sinh UID và SecretKey nếu người chơi chưa có
      let playerId = state.player.id;
      if (!playerId || !playerId.startsWith('usr_')) {
        playerId = CryptoStore.generateUID();
        state.player.id = playerId;
      }

      let secretKey = state.player.secretKey;
      if (!secretKey) {
        secretKey = CryptoStore.generateSecretKey();
        state.player.secretKey = secretKey;
      }

      const payload: PersistentSave = {
        version: 3,
        savedAt: Date.now(),
        phase: state.phase,
        player: {
          ...state.player,
          id: playerId,
          secretKey,
        },
        stalls: state.stalls,
        neighborhood: state.neighborhood,
        seed: state.seed,
        dayNumber: state.dayNumber,
        dayStats: state.dayStats,
        dayHistory: state.dayHistory,
        totalRevenue: state.player.totalRevenue ?? 0,
        totalProfit: state.player.totalProfit ?? 0,
        totalSales: state.player.totalSales ?? 0,
        dummyRivals: state.dummyRivals,
        weather: state.weather,
        currentShift: state.currentShift,
        isWeekend: state.isWeekend,
        isOvertimeActive: state.isOvertimeActive,
        dayOfWeek: state.dayOfWeek,
        narrativeQuote: state.narrativeQuote ?? undefined,
        currentTrendingTopic: state.currentTrendingTopic,
        trendingItems: state.trendingItems,
        saveSeq: localSaveSeq,
        secretKey,
      };
      localStorage.setItem(SAVE_KEY, JSON.stringify(payload));
      return true;
    } catch (e) {
      console.warn('[SaveStore] Failed to save state to localStorage:', e);
      return false;
    }
  }

  public static load(): PersistentSave | null {
    try {
      // 1. Thử load save v3 (đầy đủ bản đồ & chỉ số)
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as PersistentSave;
        if (parsed.version === 3) {
          if (!parsed.player.secretKey) {
            parsed.player.secretKey = CryptoStore.generateSecretKey();
          }
          if (!parsed.player.id || !parsed.player.id.startsWith('usr_')) {
            parsed.player.id = CryptoStore.generateUID();
          }
          if (!parsed.player.stallLevel) {
            parsed.player.stallLevel = 1;
          }
          if (!Array.isArray(parsed.player.unlockedPerks)) {
            parsed.player.unlockedPerks = [];
          }
          parsed.secretKey = parsed.player.secretKey;
          if (typeof parsed.saveSeq === 'number') {
            localSaveSeq = Math.max(localSaveSeq, parsed.saveSeq);
          }
          return parsed;
        }
      }

      // 2. Fallback load save v2 nếu người chơi đã có save từ bản trước
      const legacyRaw = localStorage.getItem(LEGACY_SAVE_KEY);
      if (legacyRaw) {
        const legacyParsed = JSON.parse(legacyRaw) as Partial<PersistentSave>;
        const sec = CryptoStore.generateSecretKey();
        const uid = CryptoStore.generateUID();
        return {
          version: 3,
          savedAt: legacyParsed.savedAt ?? Date.now(),
          phase: legacyParsed.player?.stallName ? 'PLAYING' : 'LOBBY',
          player: {
            ...(legacyParsed.player as GameState['player']),
            id: uid,
            secretKey: sec,
            stallLevel: legacyParsed.player?.stallLevel ?? 1,
            unlockedPerks: legacyParsed.player?.unlockedPerks ?? [],
          },
          stalls: legacyParsed.stalls ?? {},
          neighborhood: null,
          seed: legacyParsed.seed ?? 42,
          dayNumber: legacyParsed.dayNumber ?? 1,
          dayHistory: legacyParsed.dayHistory ?? [],
          totalRevenue: legacyParsed.totalRevenue ?? 0,
          totalProfit: legacyParsed.totalProfit ?? 0,
          totalSales: legacyParsed.totalSales ?? 0,
          secretKey: sec,
          saveSeq: 1,
        };
      }

      return null;
    } catch (e) {
      console.warn('[SaveStore] Corrupted save payload:', e);
      return null;
    }
  }

  public static importSave(imported: PersistentSave): boolean {
    try {
      imported.version = 3;
      imported.savedAt = Date.now();
      if (!imported.player.secretKey) {
        imported.player.secretKey = imported.secretKey || CryptoStore.generateSecretKey();
      }
      if (!imported.player.id || !imported.player.id.startsWith('usr_')) {
        imported.player.id = CryptoStore.generateUID();
      }
      if (!imported.player.stallLevel) {
        imported.player.stallLevel = 1;
      }
      if (!Array.isArray(imported.player.unlockedPerks)) {
        imported.player.unlockedPerks = [];
      }
      imported.secretKey = imported.player.secretKey;
      if (typeof imported.saveSeq === 'number') {
        localSaveSeq = Math.max(localSaveSeq, imported.saveSeq);
      }
      localStorage.setItem(SAVE_KEY, JSON.stringify(imported));
      return true;
    } catch (e) {
      console.warn('[SaveStore] Failed to import save:', e);
      return false;
    }
  }

  public static clear(): void {
    try {
      localStorage.removeItem(SAVE_KEY);
      localStorage.removeItem(LEGACY_SAVE_KEY);
      localSaveSeq = 0;
    } catch (e) {
      console.warn('[SaveStore] Failed to clear save:', e);
    }
  }
}
